/**
 * Room Tag Tool — "add room" (floorplan persona, Design tab).
 *
 * Creates room tags (ROOM groups): hovering a room highlights its region —
 * the room-measure polygon in MEASURED mode, or the wall-bounded interior
 * (polygon ⊖ wall-partition rings) in WALL SKETCH mode — and clicking creates
 * a tag anchored at the cursor (via the `request_room_tag` scene event).
 *
 * Existing tags stay draggable here: hovering a label within pick radius
 * switches to a move gizmo that repositions the anchor on the floor plane.
 */

import * as THREE from 'three';
import earcut from 'earcut';
import { v4 as uuid } from 'uuid';
import { FloorplanToolBase } from './FloorplanToolBase';
import { CameraMode } from '../../../defines';
import { ViewModes } from '../../helpers';
import type { IViewConfig } from '../../types';
import { DragEventData } from '../../BaseTool';
import { projectMouseToFloor } from './WallSketchSnapEngine';
import { getRoomGroupPositionProperty } from '@pointorama/potree-shared';
import { containingRoomForPoint } from '../../../utils/resolveRoomForPoint';
import { FootprintPolygon } from '../../../utils/manifoldCsg';
import { computeEnclosedFaces, faceContainingPoint } from '../../../utils/wallSketchFaces';
import { manualRoomFolderId, type RoomTagEditRequest } from '../../../../utils/roomFolders';
import type { PointoramaRoom } from '../../../utils/PointoramaRoom';

/** Screen-space pick radius around a room label's anchor (px). */
const LABEL_PICK_RADIUS_PX = 40;
/** Highlight fill color — matches the app's neon-green accent. */
const HIGHLIGHT_COLOR = 0x6ee7a0;

interface LabelEntry {
  folderId: string;
  position: THREE.Vector3;
}

export class RoomTagTool extends FloorplanToolBase {
  readonly name = 'room_tag';

  private hoveredRoom: PointoramaRoom | null = null;
  private hoveredFace: FootprintPolygon | null = null;
  private hoveredLabel: LabelEntry | null = null;
  private labelDrag: { folderId: string; z: number } | null = null;
  private highlightMesh: THREE.Mesh | null = null;
  /** Enclosed-face cache for the WALL SKETCH representation (per sketch). */
  private facesCache: { sketchId: string; faces: FootprintPolygon[] } | null = null;
  /** Saved orbit-controls dblclick-zoom state while a label is hovered. */
  private prevDoubleClickZoom: boolean | null = null;

  getViewConfig(): IViewConfig {
    return {
      views: [ViewModes.XRAY_2D, ViewModes.PLAN_2D_SKELETON, ViewModes.PERSPECTIVE_3D_RGB],
      defaultViewId: ViewModes.XRAY_2D.id,
    };
  }

  private getFloorZ(): number {
    return this.currentFloorFloorPlane?.height ?? 0;
  }

  protected onFloorChanged(): void {
    this.facesCache = null;
    this.setHoveredRoom(null);
    this.setHoveredFace(null);
  }

  /** The room label whose anchor projects within pick radius of the cursor. */
  private pickLabel(event: MouseEvent): LabelEntry | null {
    const layer = this.viewer.roomLabelsLayer;
    if (!layer) return null;
    const domElement = this.viewer.renderer.domElement;
    const rect = domElement.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    const camera = this.viewer.scene.getActiveCamera();

    let best: { entry: LabelEntry; distance: number } | null = null;
    for (const entry of layer.getLabelEntries()) {
      const projected = entry.position.clone().project(camera);
      if (projected.z > 1) continue; // behind the camera
      const sx = (projected.x * 0.5 + 0.5) * rect.width;
      const sy = (-projected.y * 0.5 + 0.5) * rect.height;
      const distance = Math.hypot(event.clientX - rect.left - sx, event.clientY - rect.top - sy);
      if (distance <= LABEL_PICK_RADIUS_PX && (!best || distance < best.distance)) {
        best = { entry, distance };
      }
    }
    return best?.entry ?? null;
  }

  /** Enclosed faces of the active floor's wall sketch (cached per sketch). */
  private enclosedFaces(): FootprintPolygon[] {
    const floorId = this.currentFloorGroupIdentifier;
    const sketch = floorId ? this.viewer.scene.wallSketches.find((s) => s.groupIdentifier === floorId) : undefined;
    if (!sketch?.identifier) return [];
    if (this.facesCache?.sketchId === sketch.identifier) return this.facesCache.faces;
    const faces = computeEnclosedFaces(sketch);
    if (faces === null) return []; // WASM still loading — retried on next hover
    this.facesCache = { sketchId: sketch.identifier, faces };
    return faces;
  }

  onMouseMove(event: MouseEvent): void {
    if (this.labelDrag) return;

    // Existing tag under the cursor wins — offer the move gizmo.
    this.hoveredLabel = this.pickLabel(event);
    // Absorb double clicks over a label (they edit the name, not zoom).
    this.setDoubleClickZoomGuard(this.hoveredLabel !== null);
    if (this.hoveredLabel) {
      // Dragging repositions on the floor plane — only meaningful (and only
      // offered) in the top-down 2D views; in 3D a tag is click/edit only.
      this.viewer.renderer.domElement.style.cursor = this.is2DMode() ? 'move' : 'pointer';
      this.setHoveredRoom(null);
      this.setHoveredFace(null);
      return;
    }

    const floorId = this.currentFloorGroupIdentifier;
    const world = floorId ? projectMouseToFloor(event, this.viewer.renderer.domElement, this.viewer.scene.getActiveCamera(), this.getFloorZ()) : null;

    // MEASURED → room-measure polygons; WALL SKETCH → the sketch's enclosed
    // spaces (no measure required).
    const isOriginal = this.viewer.floorplanRepresentation?.isOriginalActive(floorId) ?? true;
    if (isOriginal) {
      const room = world && floorId ? containingRoomForPoint(this.viewer, floorId, world) : null;
      this.setHoveredFace(null);
      this.setHoveredRoom(room);
      this.viewer.renderer.domElement.style.cursor = room ? 'copy' : 'default';
    } else {
      const face = world ? faceContainingPoint(this.enclosedFaces(), world) : null;
      this.setHoveredRoom(null);
      this.setHoveredFace(face);
      this.viewer.renderer.domElement.style.cursor = face ? 'copy' : 'default';
    }
  }

  onClick(event: MouseEvent): void {
    if (this.labelDrag) return;
    // Re-pick at click time: the hover state can be stale when a label was
    // hidden behind the inline editor and reappeared on the commit that this
    // very click triggered (document pointerdown runs before the click).
    this.hoveredLabel = this.pickLabel(event);
    if (this.hoveredLabel) {
      // Plain click (no drag) on an existing tag → select it in the left
      // panel; editing needs a double click (see onDoubleClick).
      this.viewer.scene.dispatchEvent({ type: 'room_tag_select', value: { folderId: this.hoveredLabel.folderId } });
      return;
    }
    const floorId = this.currentFloorGroupIdentifier;
    if (!floorId || (!this.hoveredRoom && !this.hoveredFace)) return;
    const world = projectMouseToFloor(event, this.viewer.renderer.domElement, this.viewer.scene.getActiveCamera(), this.getFloorZ());
    if (!world) return;
    this.viewer.scene.dispatchEvent({
      type: 'request_room_tag',
      value: {
        identifier: manualRoomFolderId(uuid()),
        floorGroupIdentifier: floorId,
        position: { x: world.x, y: world.y, z: this.getFloorZ() },
      },
    });
  }

  onDoubleClick(): void {
    if (this.labelDrag || !this.hoveredLabel) return;
    // Double click on an existing tag → edit its name inline (handled by
    // RoomTagNameEditor).
    const { folderId, position } = this.hoveredLabel;
    const folder = this.viewer.roomLabelsLayer?.getFolders().find((f) => f.identifier === folderId);
    this.viewer.scene.dispatchEvent({
      type: 'room_tag_edit',
      value: { folderId, name: folder?.name ?? 'Room', position: { x: position.x, y: position.y, z: position.z } } satisfies RoomTagEditRequest,
    });
  }

  /** Disable orbit dblclick-zoom while hovering a label; restore off it. */
  private setDoubleClickZoomGuard(hovering: boolean): void {
    const controls = this.viewer.orbitControls;
    if (!controls) return;
    if (hovering && this.prevDoubleClickZoom === null) {
      this.prevDoubleClickZoom = controls.doubleClickZoomEnabled;
      controls.doubleClickZoomEnabled = false;
    } else if (!hovering && this.prevDoubleClickZoom !== null) {
      controls.doubleClickZoomEnabled = this.prevDoubleClickZoom;
      this.prevDoubleClickZoom = null;
    }
  }

  private is2DMode(): boolean {
    return this.viewer.scene.cameraMode === CameraMode.ORTHOGRAPHIC;
  }

  onDragStart(data: DragEventData): void {
    if (data.button !== 0 || !this.hoveredLabel || !this.is2DMode()) return;
    this.labelDrag = { folderId: this.hoveredLabel.folderId, z: this.hoveredLabel.position.z };
    this.viewer.inputHandler?.blockOrbitControls?.();
  }

  onDrag(data: DragEventData): void {
    if (!this.labelDrag) return;
    const world = projectMouseToFloor(data.event, this.viewer.renderer.domElement, this.viewer.scene.getActiveCamera(), this.labelDrag.z);
    if (!world) return;
    try {
      // Live write — the labels layer follows via its position subscription,
      // and the property pipeline persists (debounced) + syncs cross-tab.
      getRoomGroupPositionProperty(this.labelDrag.folderId).value = { x: world.x, y: world.y, z: this.labelDrag.z };
    } catch {
      // property service not ready — ignore this frame
    }
  }

  onDragEnd(): void {
    if (!this.labelDrag) return;
    this.labelDrag = null;
    this.viewer.inputHandler?.unblockOrbitControls?.();
  }

  // ─── Hover highlight ──────────────────────────────────────────────────

  private setHoveredRoom(room: PointoramaRoom | null): void {
    if (room === this.hoveredRoom) return;
    this.hoveredRoom = room;
    this.clearHighlight();
    if (!room) return;
    const polygon = room.getPolygon();
    if (polygon.length < 3) return;
    this.buildHighlight([{ outer: polygon.map((p) => [p.x, p.y] as [number, number]), holes: [] }]);
  }

  private setHoveredFace(face: FootprintPolygon | null): void {
    if (face === this.hoveredFace) return;
    this.hoveredFace = face;
    if (this.hoveredRoom) return; // room highlight owns the mesh
    this.clearHighlight();
    if (face) this.buildHighlight([face]);
  }

  /** Fill the given region(s) with the translucent hover highlight. */
  private buildHighlight(footprint: FootprintPolygon[]): void {
    const z = this.getFloorZ() + 0.05;
    const vertices: number[] = [];
    for (const poly of footprint) {
      const flat: number[] = [];
      const holeIdx: number[] = [];
      for (const p of poly.outer) flat.push(p[0], p[1]);
      for (const hole of poly.holes) {
        holeIdx.push(flat.length / 2);
        for (const p of hole) flat.push(p[0], p[1]);
      }
      const tris = earcut(flat, holeIdx.length > 0 ? holeIdx : null, 2);
      for (let i = 0; i < tris.length; i += 3) {
        for (const idx of [tris[i], tris[i + 1], tris[i + 2]]) {
          vertices.push(flat[idx * 2], flat[idx * 2 + 1], z);
        }
      }
    }
    if (vertices.length === 0) return;

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(vertices), 3));
    const material = new THREE.MeshBasicMaterial({
      color: HIGHLIGHT_COLOR,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.3,
      depthTest: false,
      depthWrite: false,
    });
    this.highlightMesh = new THREE.Mesh(geometry, material);
    this.highlightMesh.renderOrder = 4;
    this.highlightMesh.frustumCulled = false;
    this.viewer.floorplanTool?.scene?.add(this.highlightMesh);
  }

  private clearHighlight(): void {
    if (!this.highlightMesh) return;
    this.highlightMesh.parent?.remove(this.highlightMesh);
    this.highlightMesh.geometry.dispose();
    (this.highlightMesh.material as THREE.Material).dispose();
    this.highlightMesh = null;
  }

  protected onDisable(): void {
    if (this.labelDrag) {
      this.labelDrag = null;
      this.viewer.inputHandler?.unblockOrbitControls?.();
    }
    this.hoveredLabel = null;
    this.setDoubleClickZoomGuard(false);
    this.setHoveredRoom(null);
    this.setHoveredFace(null);
    this.facesCache = null;
    this.viewer.renderer.domElement.style.cursor = 'default';
    super.onDisable();
  }

  dispose(): void {
    this.clearHighlight();
    super.dispose();
  }
}
